import { MonetizationOn } from "@mui/icons-material";
import React from "react";

const PriceSidebar = ({
  pricingInfo,
  pricingResponse,
  formatCurrency,
  formatNumber,
  totalTax,
  selectedBaggage,
  selectedMeals,
  selectedSeats,
  handleProceedToBooking,
  getSegmentPath,
  charges,
  totalPrice,
}) => {
  return (
    <div className="lg:col-span-1">
      <div className="sticky top-8 p-6 bg-white rounded-2xl border border-gray-200 shadow-sm">
        <h3 className="flex items-center mb-6 text-xl font-semibold text-gray-900">
          <MonetizationOn className="mr-2 w-5 h-5 text-main-800" />
          Final Price Summary
        </h3>

        <div className="space-y-4 text-black">
          {pricingInfo?.baseFare?.amount && (
            <div className="flex justify-between">
              <span className="text-gray-600">Base Fare</span>
              <span className="font-medium">
                {formatCurrency(
                  pricingInfo.baseFare.amount,
                  pricingInfo.baseFare.currency
                )}
              </span>
            </div>
          )}

          {pricingResponse?.pricing?.passengers?.map(
            (passenger, passengerIndex) => (
              <div key={passengerIndex}>
                {passenger.fees?.map((fee, feeIndex) => (
                  <div key={feeIndex} className="flex justify-between">
                    <span className="text-gray-600">
                      {fee.code} - {passenger.passengerType?.code}
                    </span>
                    <span className="font-medium text-main-900">
                      {formatCurrency(fee.amount, fee.currency)}
                    </span>
                  </div>
                ))}
              </div>
            )
          )}

          <div className="flex justify-between">
            <div>
              <span className="text-gray-600">Taxes & Charges:</span>
            </div>
            <span className="font-medium text-main-900">
              {pricingResponse?.pricing?.totals?.totalFare?.currency}{" "}
              {formatNumber(totalTax)}
            </span>
          </div>

          <div className="mt-4 space-y-3">
            {Object.keys(selectedBaggage).length > 0 && (
              <div>
                <h5 className="mb-2 text-sm font-medium text-gray-700">
                  Selected Baggage:
                </h5>
                <div className="space-y-1">
                  {Object.entries(selectedBaggage).map(
                    ([passengerIndex, segments]) =>
                      Object.entries(segments).map(
                        ([segmentIndex, baggage]) => {
                          const segments = pricingResponse?.flights || [];
                          const segment = segments[segmentIndex];
                          const segmentPath = segment
                            ? getSegmentPath(segment)
                            : `Segment ${parseInt(segmentIndex) + 1}`;

                          return (
                            <div
                              key={`baggage-${passengerIndex}-${segmentIndex}`}
                              className="flex justify-between text-sm"
                            >
                              <span className="text-gray-600">
                                Passenger {parseInt(passengerIndex) + 1} -{" "}
                                {segmentPath}
                              </span>
                              <span className="font-medium text-main-900">
                                {baggage.baggageDescription}
                              </span>
                            </div>
                          );
                        }
                      )
                  )}
                </div>
              </div>
            )}

            {Object.keys(selectedMeals).length > 0 && (
              <div>
                <h5 className="mb-2 text-sm font-medium text-gray-700">
                  Selected Meals:
                </h5>
                <div className="space-y-1">
                  {Object.entries(selectedMeals).map(
                    ([passengerIndex, segments]) =>
                      Object.entries(segments).map(([segmentIndex, meal]) => {
                        const segments = pricingResponse?.flights || [];
                        const segment = segments[segmentIndex];
                        const segmentPath = segment
                          ? getSegmentPath(segment)
                          : `Segment ${parseInt(segmentIndex) + 1}`;

                        return (
                          <div
                            key={`meal-${passengerIndex}-${segmentIndex}`}
                            className="flex justify-between text-sm"
                          >
                            <span className="text-gray-600">
                              Passenger {parseInt(passengerIndex) + 1} -{" "}
                              {segmentPath}
                            </span>
                            <span className="font-medium text-main-900">
                              {meal.mealName} - price: {meal.mealCharge}
                            </span>
                          </div>
                        );
                      })
                  )}
                </div>
              </div>
            )}

            {Object.keys(selectedSeats).length > 0 && (
              <div>
                <h5 className="mb-2 text-sm font-medium text-gray-700">
                  Selected Seats:
                </h5>
                <div className="space-y-1">
                  {Object.entries(selectedSeats).map(
                    ([passengerIndex, segments]) =>
                      Object.entries(segments).map(([segmentIndex, seat]) => {
                        const segments = pricingResponse?.flights || [];
                        const segment = segments[segmentIndex];
                        const segmentPath = segment
                          ? getSegmentPath(segment)
                          : `Segment ${parseInt(segmentIndex) + 1}`;

                        return (
                          <div
                            key={`seat-${passengerIndex}-${segmentIndex}`}
                            className="flex justify-between text-sm"
                          >
                            <span className="text-gray-600">
                              Passenger {parseInt(passengerIndex) + 1} -{" "}
                              {segmentPath}
                            </span>
                            <span className="font-medium text-main-900">
                              {seat.rowNumber}
                              {seat.seatNumber}
                              {"-"} price: {seat.seatCharacteristics}
                            </span>
                          </div>
                        );
                      })
                  )}
                </div>
              </div>
            )}
          </div>

          <hr />
          <div className="flex justify-between mt-2 space-y-2">
            <div>
              <span className="text-gray-600">Service Charge</span>
            </div>
            <span className="font-medium text-main-900">
              {pricingResponse?.pricing?.totals?.totalFare?.currency}{" "}
              {formatNumber(charges || 0)}
            </span>
          </div>

          <hr className="border-gray-200" />

          <div className="flex justify-between items-center">
            <span className="text-lg font-semibold text-gray-900">
              Total Amount
            </span>
            <div className="text-right">
              <div className="text-2xl font-semibold text-main-900">
                {pricingResponse?.pricing?.totals?.totalFare?.currency}{" "}
                {formatNumber(totalPrice())}
              </div>
            </div>
          </div>

          <button
            onClick={handleProceedToBooking}
            className="flex justify-center items-center p-2 space-x-2 w-full font-semibold text-white rounded-lg shadow-lg transition-colors duration-200 bg-main-900 hover:bg-main-900 hover:shadow-xl"
          >
            <span>Continue</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PriceSidebar;
